import {
  getCurrentFocusKey,
  setFocus,
} from '@noriginmedia/norigin-spatial-navigation'
import React, {
  createContext,
  useCallback,
  useContext,
  useReducer,
  useRef,
} from 'react'
import ErrorModalTS from '../../../components/ErrorModal'
import { ErrorModalProps } from '../../../components/ErrorModal/types'
import {
  ErrorModalActions,
  ErrorModalContextType,
  ErrorModalProviderProps,
  ErrorModalSettingsState,
} from './types'

const ErrorModalContext = createContext<ErrorModalContextType | null>(null)

const initialState: ErrorModalSettingsState = {
  showErrorModal: false,
  modalProps: {
    title: '',
    description: '',
    buttons: [],
    hideStationLogo: false,
    error: null,
  },
}

const errorModalReducer = (
  state: ErrorModalSettingsState,
  action: ErrorModalActions
): ErrorModalSettingsState => {
  switch (action.type) {
    case 'SHOW_MODAL':
      return { ...state, showErrorModal: true }
    case 'HIDE_MODAL':
      return {
        ...state,
        showErrorModal: false,
        modalProps: initialState.modalProps,
      }
    case 'SET_MODAL_PROPS':
      return { ...state, modalProps: { ...action.payload } }
    default:
      return state
  }
}

const ErrorModalProvider = (props: ErrorModalProviderProps) => {
  const [errorModalSettings, dispatch] = useReducer(
    errorModalReducer,
    initialState
  )
  const lastFocusedKeyRef = useRef<string | null>(null)

  const setErrorModalVisible = () => {
    const currentFocusKey = getCurrentFocusKey()
    lastFocusedKeyRef.current = currentFocusKey

    dispatch({ type: 'SHOW_MODAL' })
  }

  const setErrorModalNotVisible = useCallback(() => {
    dispatch({ type: 'HIDE_MODAL' })

    if (lastFocusedKeyRef.current) {
      setFocus(lastFocusedKeyRef.current)
      lastFocusedKeyRef.current = null
    }
  }, [])

  const setErrorModalProps = (props: ErrorModalProps) => {
    dispatch({ type: 'SET_MODAL_PROPS', payload: props })
  }

  const { modalProps, showErrorModal } = errorModalSettings
  return (
    <ErrorModalContext.Provider
      value={{
        setErrorModalVisible,
        setErrorModalNotVisible,
        setErrorModalProps,
      }}
    >
      {showErrorModal && (
        <ErrorModalTS
          title={modalProps.title}
          description={modalProps.description}
          buttons={modalProps.buttons}
          hideStationLogo={modalProps.hideStationLogo}
          error={modalProps.error}
        />
      )}
      {props.children}
    </ErrorModalContext.Provider>
  )
}

export const useErrorModal = () => {
  const context = useContext(ErrorModalContext)

  if (!context) {
    throw new Error('useErrorModal must be used within an ErrorModalProvider')
  }

  return context
}

export default ErrorModalProvider
